/* Safe DOM helpers — single source of truth for HTML escaping (F-17).
 *
 * Three primitives:
 *   escapeHtml(s)     → string-safe for interpolation inside textContent contexts
 *                       embedded in innerHTML. Escapes &<>"'`/ — a superset of
 *                       the OWASP-recommended set so a stray ` or / never
 *                       breaks an attribute boundary.
 *   escapeAttr(s)     → same as escapeHtml but also handles single quotes for
 *                       single-quoted attribute contexts. Use inside `attr='...'`.
 *   safeHtml(node, h) → DOMPurify-sanitized innerHTML. Use when h already
 *                       contains markup that must remain markup.
 *
 * Three modules used to roll their own escapeHtml in slightly different
 * ways; this file replaces all of them so a future XSS fix lands in one place.
 */

import DOMPurify from 'dompurify';

const ENTITY_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '`': '&#96;',
  '/': '&#x2F;',
};

export function escapeHtml(s: unknown): string {
  return String(s ?? '').replace(/[&<>"'`/]/g, (c) => ENTITY_MAP[c]);
}

export function escapeAttr(s: unknown): string {
  return escapeHtml(s);
}

/** Set sanitized markup on a node. Strips <script>, on*= handlers, javascript:
 *  URLs, etc. Use this when you've assembled an HTML string containing trusted
 *  markup mixed with user-controlled text. */
export function safeHtml(node: HTMLElement, html: string): void {
  node.innerHTML = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

/** Set plain text — never use this with HTML, the node will display markup as-is. */
export function safeText(node: HTMLElement, s: unknown): void {
  node.textContent = String(s ?? '');
}
